/**
 * 打开专属客服：使用 base_info 返回的 kefu / kefu_contact.url
 */
import {
  resolveKefuDisplayName,
  resolveMemberId,
} from "@/common/kefuChat";

export function getKefuContact(baseInfo) {
  const info = baseInfo || {};
  const contact = info.kefu_contact || {};
  const url = contact.url || info.kefu || "";
  return {
    mode: contact.mode || "contact_only",
    title: contact.title || "",
    action_text: contact.action_text || "",
    enabled: contact.enabled == null ? 1 : Number(contact.enabled),
    url: String(url || "").trim(),
    name: contact.name || "",
  };
}

function readBaseInfo(vm) {
  const store = vm && vm.$store;
  const getters = (store && store.getters) || {};
  const state = (store && store.state) || {};
  return getters.getBaseInfo || state.baseInfo || {};
}

function kefuText(vm, key, fallback) {
  try {
    if (vm && typeof vm.$t === "function") {
      const text = vm.$t(key);
      if (text && text !== key) {
        return String(text);
      }
    }
  } catch (error) {
    /* ignore */
  }
  return fallback;
}

/**
 * 后台常填 www.xxx.com（无协议）。若不补 https://，
 * 浏览器会当成当前站相对路径，表现为「跳完又回首页」。
 */
export function normalizeKefuUrl(url) {
  let target = String(url || "").trim();
  if (!target) {
    return "";
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(target)) {
    return target;
  }
  if (target.startsWith("//")) {
    return `https:${target}`;
  }
  return `https://${target.replace(/^\/+/, "")}`;
}

/** 若接口未带上身份参数，前端按约定补 userId / userName */
export function withKefuIdentity(url, userId, userName) {
  const target = normalizeKefuUrl(url);
  if (!target || !userId) {
    return target;
  }
  try {
    const parsed = new URL(target);
    if (!parsed.searchParams.get("userId")) {
      parsed.searchParams.set("userId", String(userId));
    }
    const name = String(userName || userId).trim();
    if (name && !parsed.searchParams.get("userName")) {
      parsed.searchParams.set("userName", name);
    }
    return parsed.toString();
  } catch (error) {
    return target;
  }
}

/**
 * 打开接口下发的客服链接（整页跳转，不用 iframe）。
 */
export function openKefuUrl(vm, url, options = {}) {
  const target = normalizeKefuUrl(url);
  if (!target) {
    return false;
  }

  if (options.newTab) {
    const win = window.open(target, "_blank");
    return !!win;
  }

  window.location.href = target;
  return true;
}

export async function openKefuContact(vm, options = {}) {
  const connectingText = kefuText(
    vm,
    "my.kefu_connecting",
    "Connecting to support…"
  );
  const loginRequiredText = kefuText(
    vm,
    "my.kefu_login_required",
    "Please log in before contacting support"
  );
  const unavailableText = kefuText(
    vm,
    "my.kefu_unavailable",
    "Support is temporarily unavailable. Please try again later"
  );

  if (vm && vm.$toast && !options.silent) {
    vm.$toast.loading({
      message: options.loadingText || connectingText,
      forbidClick: true,
      duration: 0,
    });
  }

  try {
    let userId = String(options.userId || resolveMemberId(vm) || "").trim();
    // 已登录但本地还没有 ID 时，补拉一次会员信息
    if (vm && vm.$http && localStorage.getItem("token")) {
      try {
        const res = await vm.$http({ method: "get", url: "user_info" });
        if (res && res.code === 200 && res.data) {
          if (vm.$store && vm.$store.commit) {
            vm.$store.commit("setUserInfoValue", res.data);
          }
          if (res.data.member_code) {
            try {
              localStorage.setItem("member_code", String(res.data.member_code));
            } catch (error) {
              /* ignore */
            }
          }
          const nextId = resolveMemberId(vm);
          if (nextId) {
            userId = nextId;
          } else if (!userId) {
            userId = String(
              res.data.member_code ||
                res.data.id ||
                res.data.user_id ||
                res.data.uid ||
                ""
            ).trim();
          }
        }
      } catch (error) {
        // ignore
      }
    }

    if (!userId) {
      if (vm && vm.$toast) {
        vm.$toast.clear();
        vm.$toast(loginRequiredText);
      }
      if (vm && vm.$router) {
        vm.$router.push(options.loginPath || "/Login");
      }
      return false;
    }

    const contact = getKefuContact(readBaseInfo(vm));
    const visitorName = resolveKefuDisplayName(vm, userId);
    const rawUrl = String(options.url || contact.url || "").trim();
    const url = withKefuIdentity(rawUrl, userId, visitorName);

    // eslint-disable-next-line no-console
    console.log(
      "[kefu] customerId=",
      userId,
      "name=",
      visitorName,
      "raw=",
      rawUrl,
      "url=",
      url
    );

    if (contact.enabled === 0 && !options.url) {
      if (vm && vm.$toast) {
        vm.$toast.clear();
        vm.$toast(unavailableText);
      }
      return false;
    }

    if (!url) {
      if (vm && vm.$toast) {
        vm.$toast.clear();
        vm.$toast(unavailableText);
      }
      return false;
    }

    const opened = openKefuUrl(vm, url, options);

    if (vm && vm.$toast) {
      vm.$toast.clear();
    }
    return !!opened;
  } catch (error) {
    if (vm && vm.$toast) {
      vm.$toast.clear();
      vm.$toast(unavailableText);
    }
    // eslint-disable-next-line no-console
    console.error("[kefu] open failed", error);
    return false;
  }
}

export function resolveLanguageOptions(baseInfo, fallbackOptions) {
  const allowed = fallbackOptions || [];
  const list = (baseInfo && Array.isArray(baseInfo.lang_list) && baseInfo.lang_list) || [];
  if (!list.length) {
    return allowed;
  }

  const byCode = {};
  allowed.forEach((item) => {
    byCode[item.code] = item;
    byCode[item.appLang] = item;
  });

  const mapped = list
    .map((item) => {
      const code = item.code || item.lang || item.value;
      const known = byCode[code];
      if (!known) {
        return null;
      }
      return {
        ...known,
        fullLabel: item.name || known.fullLabel,
        label: known.label,
      };
    })
    .filter(Boolean);

  const present = new Set(mapped.map((item) => item.code));
  allowed.forEach((item) => {
    if (!present.has(item.code)) {
      mapped.push(item);
    }
  });

  return mapped.length ? mapped : allowed;
}
